import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTier } from '../../context/TierContext';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Truck, Wrench, Shield, Users, DollarSign, Layers, Lock, Factory } from 'lucide-react';

// Tab components
import AssetTypesTab from './AssetTypesTab';
import AssetMakesModelsTab from './AssetMakesModelsTab';
import AttachmentTypesTab from './AttachmentTypesTab';
import ComplianceTypesTab from './ComplianceTypesTab';
import WorkerTypesTab from './WorkerTypesTab';
import PriceListsTab from './PriceListsTab';
import MaterialTypesTab from './MaterialTypesTab';

const DataManagement = () => {
  const { api } = useAuth();
  const { hasFeature } = useTier();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'equipment-types');
  const [loading, setLoading] = useState(true);

  // Shared data state
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentMakes, setEquipmentMakes] = useState([]);
  const [equipmentModels, setEquipmentModels] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [complianceTypes, setComplianceTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [taxSettings, setTaxSettings] = useState({
    tax_name: 'GST',
    tax_rate: 10,
    currency_symbol: '$'
  });

  // Fetch functions
  const fetchEquipmentTypes = async () => {
    try {
      const response = await api.get('/equipment-types/all');
      setEquipmentTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment types', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipmentMakes = async () => {
    try {
      const response = await api.get('/equipment-makes');
      setEquipmentMakes(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment makes', error);
    }
  };

  const fetchEquipmentModels = async () => {
    try {
      const response = await api.get('/equipment-models');
      setEquipmentModels(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment models', error);
    }
  };

  const fetchAttachmentTypes = async () => {
    try {
      const response = await api.get('/attachment-types/all');
      setAttachmentTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch attachment types', error);
    }
  };

  const fetchComplianceTypes = async () => {
    try {
      const response = await api.get('/compliance-types/all');
      setComplianceTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch compliance types', error);
    }
  };

  const fetchWorkerTypes = async () => {
    try {
      const response = await api.get('/worker-types/all');
      setWorkerTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch worker types', error);
    }
  };

  const fetchPriceLists = async () => {
    try {
      const response = await api.get('/price-lists');
      setPriceLists(response.data);
    } catch (error) {
      console.error('Failed to fetch price lists', error);
    }
  };

  const fetchMaterialTypes = async () => {
    try {
      const response = await api.get('/material-types');
      setMaterialTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch material types', error);
    }
  };

  const fetchTaxSettings = async () => {
    try {
      const response = await api.get('/tenant/tax-settings');
      setTaxSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch tax settings', error);
    }
  };

  useEffect(() => {
    fetchEquipmentTypes();
    fetchEquipmentMakes();
    fetchEquipmentModels();
    fetchAttachmentTypes();
    fetchComplianceTypes();
    fetchWorkerTypes();
    fetchPriceLists();
    fetchMaterialTypes();
    fetchTaxSettings();
  }, [api]);

  // Handle tab change and update URL
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="data-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Data Management</h1>
          <p className="text-muted-foreground">Manage system lookup data and configurations</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="equipment-types" data-testid="tab-equipment-types">
            <Truck className="h-4 w-4 mr-2" />
            Asset Types
          </TabsTrigger>
          <TabsTrigger value="makes-models" data-testid="tab-makes-models">
            <Factory className="h-4 w-4 mr-2" />
            Makes & Models
          </TabsTrigger>
          <TabsTrigger value="attachment-types" data-testid="tab-attachment-types">
            <Wrench className="h-4 w-4 mr-2" />
            Attachment Types
          </TabsTrigger>
          <TabsTrigger 
            value="compliance-types" 
            data-testid="tab-compliance-types"
            className="relative"
          >
            <Shield className="h-4 w-4 mr-2" />
            Compliance Types
            {!hasFeature('compliance') && <Lock className="h-3 w-3 ml-1 text-muted-foreground" />}
          </TabsTrigger>
          <TabsTrigger 
            value="worker-types" 
            data-testid="tab-worker-types"
            className="relative"
          >
            <Users className="h-4 w-4 mr-2" />
            Worker Types
            {!hasFeature('worker_types') && <Lock className="h-3 w-3 ml-1 text-muted-foreground" />}
          </TabsTrigger>
          <TabsTrigger value="price-lists" data-testid="tab-price-lists">
            <DollarSign className="h-4 w-4 mr-2" />
            Price Lists
          </TabsTrigger>
          <TabsTrigger value="material-types" data-testid="tab-material-types">
            <Layers className="h-4 w-4 mr-2" />
            Material Types
          </TabsTrigger>
        </TabsList>

        <TabsContent value="equipment-types" className="mt-6">
          <AssetTypesTab 
            api={api} 
            equipmentTypes={equipmentTypes} 
            fetchEquipmentTypes={fetchEquipmentTypes}
          />
        </TabsContent>

        <TabsContent value="makes-models" className="mt-6">
          <AssetMakesModelsTab 
            api={api} 
            makes={equipmentMakes}
            models={equipmentModels}
            fetchMakes={fetchEquipmentMakes}
            fetchModels={fetchEquipmentModels}
          />
        </TabsContent>

        <TabsContent value="attachment-types" className="mt-6">
          <AttachmentTypesTab 
            api={api} 
            attachmentTypes={attachmentTypes}
            equipmentTypes={equipmentTypes}
            fetchAttachmentTypes={fetchAttachmentTypes}
          />
        </TabsContent>

        <TabsContent value="compliance-types" className="mt-6">
          <ComplianceTypesTab 
            api={api} 
            complianceTypes={complianceTypes}
            fetchComplianceTypes={fetchComplianceTypes}
          />
        </TabsContent>

        <TabsContent value="worker-types" className="mt-6">
          <WorkerTypesTab 
            api={api} 
            workerTypes={workerTypes}
            fetchWorkerTypes={fetchWorkerTypes}
            hasFeature={hasFeature}
          />
        </TabsContent>

        <TabsContent value="price-lists" className="mt-6">
          <PriceListsTab 
            api={api} 
            priceLists={priceLists}
            equipmentTypes={equipmentTypes}
            attachmentTypes={attachmentTypes}
            workerTypes={workerTypes}
            taxSettings={taxSettings}
            fetchPriceLists={fetchPriceLists}
          />
        </TabsContent>

        <TabsContent value="material-types" className="mt-6">
          <MaterialTypesTab 
            api={api} 
            materialTypes={materialTypes}
            fetchMaterialTypes={fetchMaterialTypes}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DataManagement;
